import { CheckboxGroup, MultiSelect } from '@medly-components/core';
import { FilterHeader, FiltersForm } from 'components/common';
import { useFilterActions } from 'components/common/filters/FiltersForm/useFilterActions';
import { AFFILIATION, CANCER_TYPE, GUIDE_SEARCH_PLACEHOLDER, GUIDE_TYPE, SELECT_CANCER_TYPE } from 'constants/text/guide';
import { useGuides } from 'hooks';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GUIDE_AFFILIATIONS } from '../constants';
import { defaultGuideFilters, isGuideFiltersApplied, useGuideFiltersContext } from './GuideFilters.context';
import type { GuideFiltersContextType } from './types';

export const GuideFilters: FC = () => {
    const { guideTypes, guideCancerTypes } = useGuides();
    const [_, setSearchParams] = useSearchParams();
    const [initialFilters, setFiltersContext] = useGuideFiltersContext();
    const [filters, setFilters] = useState<GuideFiltersContextType>(initialFilters);

    const { isDrawerOpen, toggleDrawer, handleChange, handleClear, handleApplyFilters, handleSearchQueryChange } = useFilterActions({
        filters,
        initialFilters,
        defaultFilters: defaultGuideFilters,
        setFilters,
        setFiltersContext
    });

    const _isFiltersApplied = useMemo(() => isGuideFiltersApplied(initialFilters), [initialFilters]);
    const guideTypeOptions = useMemo(() => guideTypes.map(guideType => ({ value: guideType, label: guideType })), [guideTypes]);
    const guideCancerTypesOptions = useMemo(() => guideCancerTypes.map(value => ({ value, label: value })), [guideCancerTypes]);
    const guideAffiliationOptions = useMemo(
        () => Object.entries(GUIDE_AFFILIATIONS).map(([key, value]) => ({ value: key, label: value })),
        []
    );

    useEffect(() => {
        _isFiltersApplied && setSearchParams({ tab: 'All' });
    }, [_isFiltersApplied]);

    return (
        <FiltersForm
            id="guide-filters"
            open={isDrawerOpen}
            toggleDrawer={toggleDrawer}
            searchQuery={filters.searchQuery}
            searchPlaceholder={GUIDE_SEARCH_PLACEHOLDER}
            isFiltersApplied={_isFiltersApplied}
            onSearchQueryChange={handleSearchQueryChange}
            onClearAllFilters={handleClear()}
            onApplyFilters={handleApplyFilters}
        >
            <FilterHeader label={GUIDE_TYPE} onClear={handleClear('guideTypes')} showClear={!!filters.guideTypes.length} />
            <CheckboxGroup
                fullWidth
                id="guide-type"
                values={filters.guideTypes}
                onChange={handleChange('guideTypes')}
                options={guideTypeOptions}
            />
            <FilterHeader label={AFFILIATION} onClear={handleClear('guideAffiliations')} showClear={!!filters.guideAffiliations.length} />
            <CheckboxGroup
                fullWidth
                id="guide-affiliation"
                values={filters.guideAffiliations}
                onChange={handleChange('guideAffiliations')}
                options={guideAffiliationOptions}
            />
            <FilterHeader label={CANCER_TYPE} onClear={handleClear('guideCancerTypes')} showClear={!!filters.guideCancerTypes.length} />
            <MultiSelect
                fullWidth
                variant="outlined"
                id="guide-cancer-types"
                label={CANCER_TYPE}
                placeholder={SELECT_CANCER_TYPE}
                values={filters.guideCancerTypes}
                onChange={handleChange('guideCancerTypes')}
                options={guideCancerTypesOptions}
            />
        </FiltersForm>
    );
};

GuideFilters.displayName = 'GuideFilters';
